import { ReadTrackedEvent } from '@tinct/core-api-models';
import {
  Badge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@tinct/web-components';
import { formatRelativeTime } from '@tinct/core-utils';

export interface TrackedEventsSectionProps {
  trackedEvents: ReadTrackedEvent[] | null | undefined;
  /** When undefined (loading), render nothing. */
  isReady: boolean;
  title?: string;
  emptyTitle?: string;
  emptyDescription?: string;
}

export function TrackedEventsSection({
  trackedEvents,
  isReady,
  title = 'Tracked events',
  emptyTitle = 'No blocks are configured for tracking on this campaign.',
  emptyDescription = 'Enable tracking on blocks from the editor to see event data here.',
}: TrackedEventsSectionProps) {
  if (!isReady) return null;

  const events = trackedEvents ?? [];

  if (events.length === 0) {
    return (
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-1">
          <h2 className="text-xl font-semibold tracking-tight font-heading">
            {title}
          </h2>
        </div>
        <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border py-16 text-center">
          <p className="text-sm font-medium text-foreground">{emptyTitle}</p>
          <p className="max-w-md text-sm text-muted-foreground">
            {emptyDescription}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <h2 className="text-xl font-semibold tracking-tight font-heading">
          {title}
        </h2>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[45%]">Event</TableHead>
            <TableHead className="w-[15%] text-right">Count</TableHead>
            <TableHead className="w-[15%] text-right">Ratio</TableHead>
            <TableHead className="w-[25%]">Last</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {events.map((event, index) => (
            <TableRow key={`${event.blockId ?? 'block'}-${event.eventName ?? index}`}>
              <TableCell>
                <Badge variant="secondary" className="font-mono text-xs">
                  {event.eventName ?? '—'}
                </Badge>
              </TableCell>
              <TableCell className="text-right">
                {event.count?.toLocaleString() ?? '—'}
              </TableCell>
              <TableCell className="text-right">
                {event.ratio == null ? '—' : `${(event.ratio * 100).toFixed(1)}%`}
              </TableCell>
              <TableCell>
                {event.lastSeenAt ? (
                  <span className="text-muted-foreground">
                    {formatRelativeTime(new Date(event.lastSeenAt))}
                  </span>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
